import { useState, useCallback, useEffect } from 'react';
import { supabase } from '../config/supabaseClient';
import type { ServiceCustomerData } from '../components/Agent/ServiceCustomerCard';
import type { ServiceMachineData } from '../components/Agent/ServiceMachineCard';
import type { ServicePackageData } from '../components/Agent/ServicePackageCard';
import type { CustomerInfoFormValues, CustomerInfoFormPrefill } from '../components/Agent/CustomerInfoForm';
import { agentStorageKey, withHub, withMachine } from './agentMachineContext';
import type { AgentMachineContext, HubIdentity } from './agentMachineContext';

export interface EquipmentCardData {
  type: 'equipment';
  title: string;
  stale_warning?: boolean;
  data: {
    stock_number: string;
    make: string;
    model: string;
    model_year: number | string | null;
    equipment_type: string;
    status: string;
    price: number | null;
    unit_hours: number | null;
    description: string | null;
    options: string[];
    location: string | null;
    location_name: string | null;
  };
}

export interface PartBranchStock {
  location_id: number | null;
  location_name: string | null;
  quantity: number;
}

export type PartAvailabilityStatus =
  | 'in_stock'
  | 'transfer_needed'
  | 'vendor_order_needed';

export interface PartCardData {
  type: 'part';
  title: string;
  data: {
    part_number: string;
    description: string | null;
    long_description?: string | null;
    image_url: string | null;
    unit_price: number | null;
    location_name: string | null;
    quantity_at_branch: number;
    quantity_across_all: number;
    availability_status: PartAvailabilityStatus;
    branches?: PartBranchStock[];
    compatibility?: string[];
  };
}

export interface ServiceCustomerConfirmCardData {
  type: 'customer_confirm';
  data: ServiceCustomerData;
}

export interface ServiceMachineSelectionCardData {
  type: 'machine_selection';
  data: { machines: ServiceMachineData[] };
}

export interface ServicePackageSelectionCardData {
  type: 'package_selection';
  data: {
    packages: ServicePackageData[];
    equipment?: { make_name?: string; machine_name?: string };
  };
}

export interface ServiceEquipmentConfirmCardData {
  type: 'equipment_confirm';
  data: {
    make_name?: string;
    make_id?: number;
    machine_name?: string;
    machine_id?: number;
    category_id?: number;
  };
}

export interface CustomerInfoFormCardData {
  type: 'customer_info_form';
  data: {
    prefill?: CustomerInfoFormPrefill;
  };
}

export interface VerificationCodePromptCardData {
  type: 'verification_code_prompt';
  data: {
    channel: 'sms' | 'email';
    masked_destination: string;
    can_resend?: boolean;
    error?: string | null;
  };
}

export type ServiceCardData =
  | ServiceCustomerConfirmCardData
  | ServiceMachineSelectionCardData
  | ServicePackageSelectionCardData
  | ServiceEquipmentConfirmCardData
  | CustomerInfoFormCardData
  | VerificationCodePromptCardData;

export type AgentCardData = EquipmentCardData | PartCardData | ServiceCardData;

export interface CartItem {
  part_number: string;
  description: string;
  quantity: number;
  unit_price: number | null;
  quantity_at_branch: number;
  quantity_across_all: number;
  availability_status: string;
}

export interface CartSnapshot {
  location_id: number | null;
  location_name: string | null;
  items: CartItem[];
  total: number | null;
}

export interface AgentMessage {
  id: string;
  role: 'assistant' | 'user';
  content: string;
  timestamp: Date;
  agentType?: string;
  actionCards?: AgentCardData[];
  cartSnapshot?: CartSnapshot | null;
}

interface AgentChatResponse {
  message: string;
  conversation_id: string;
  active_agent: string;
  complete: boolean;
  lead_id: number | null;
  contact_collected?: boolean;
  suggested_actions: string[] | null;
  action_cards?: AgentCardData[] | null;
  cart_snapshot?: CartSnapshot | null;
  error?: string;
}

interface ServiceChatResponse {
  message: string;
  conversation_id: string;
  complete: boolean;
  state?: Record<string, unknown>;
  request_id?: number | null;
  pending_request_id?: number | null;
  action_card?: { type: string; data: any } | null;
  machines?: ServiceMachineData[] | null;
  packages?: ServicePackageData[] | null;
  diagnostic_state?: Record<string, unknown> | null;
  error?: string;
}

interface UseAgentConversationReturn {
  messages: AgentMessage[];
  conversationId: string | null;
  activeAgent: string;
  loading: boolean;
  error: string | null;
  isComplete: boolean;
  suggestedActions: string[];
  cart: CartSnapshot | null;
  sendMessage: (message: string) => Promise<void>;
  submitCustomerInfoForm: (values: CustomerInfoFormValues) => Promise<void>;
  submitVerificationCode: (code: string) => Promise<void>;
  skipVerification: () => Promise<void>;
  resendVerificationCode: () => Promise<void>;
  endConversation: () => Promise<void>;
  clearError: () => void;
}

const AGENT_EDGE_FUNCTION = 'agent-chat';
const SERVICE_EDGE_FUNCTION = 'public-service-request';

const DEFAULT_SUGGESTIONS = ['Check your hours', 'I need parts', 'Schedule service', 'Talk to sales'];

type ConversationMode = 'agent' | 'service';

// ---------------------------------------------------------------------------
// Resume persistence: conversation ids + rendered history survive a reload or
// tab close (the server captures the lead progressively either way; this is
// purely resume UX). All storage access is try/catch'd — third-party embed
// contexts can deny localStorage entirely.
// ---------------------------------------------------------------------------

const STORAGE_VERSION = 1;
const STORAGE_TTL_MS = 24 * 60 * 60 * 1000;

interface PersistedChat {
  v: number;
  conversationId: string | null;
  serviceConversationId: string | null;
  mode: ConversationMode;
  activeAgent: string;
  isComplete: boolean;
  contactCollected: boolean;
  messages: Array<Omit<AgentMessage, 'timestamp'> & { timestamp: string }>;
  cart: CartSnapshot | null;
  updatedAt: number;
}

function loadPersistedChat(key: string): PersistedChat | null {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const data = JSON.parse(raw) as PersistedChat;
    if (data?.v !== STORAGE_VERSION) return null;
    if (!data.updatedAt || Date.now() - data.updatedAt > STORAGE_TTL_MS) return null;
    if (data.isComplete) return null;
    return data;
  } catch {
    return null;
  }
}

function clearPersistedChat(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // storage unavailable — nothing to clear
  }
}

/**
 * `machineContext` (machine QR hub) rides every agent chat call and scopes the resume key to the sticker.
 * `hubIdentity` is the verified hub session for the service hand-off; it is never persisted.
 */
export const useAgentConversation = (
  organizationId: string,
  options: { machineContext?: AgentMachineContext | null; hubIdentity?: HubIdentity | null } = {},
): UseAgentConversationReturn => {
  const machineContext = options.machineContext ?? null;
  const hubIdentity = options.hubIdentity ?? null;
  const storageKey = agentStorageKey(organizationId, machineContext, hubIdentity != null);
  const [persisted] = useState<PersistedChat | null>(() => loadPersistedChat(storageKey));
  const [messages, setMessages] = useState<AgentMessage[]>(() =>
    (persisted?.messages || []).map((m) => ({ ...m, timestamp: new Date(m.timestamp) })),
  );
  const [conversationId, setConversationId] = useState<string | null>(persisted?.conversationId ?? null);
  const [serviceConversationId, setServiceConversationId] = useState<string | null>(persisted?.serviceConversationId ?? null);
  const [mode, setMode] = useState<ConversationMode>(persisted?.mode ?? 'agent');
  const [activeAgent, setActiveAgent] = useState<string>(persisted?.activeAgent ?? 'general');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  const [suggestedActions, setSuggestedActions] = useState<string[]>(
    persisted?.messages?.length ? [] : DEFAULT_SUGGESTIONS,
  );
  const [cart, setCart] = useState<CartSnapshot | null>(persisted?.cart ?? null);
  const [contactCollected, setContactCollected] = useState(persisted?.contactCollected ?? false);
  const [endNudgeShown, setEndNudgeShown] = useState(false);

  // Persist the resumable snapshot after every meaningful change; clear it
  // once the conversation ends.
  useEffect(() => {
    if (isComplete) {
      clearPersistedChat(storageKey);
      return;
    }
    if (!conversationId && !serviceConversationId) return;
    try {
      const payload: PersistedChat = {
        v: STORAGE_VERSION,
        conversationId,
        serviceConversationId,
        mode,
        activeAgent,
        isComplete,
        contactCollected,
        messages: messages.slice(-50).map((m) => ({
          ...m,
          timestamp: m.timestamp instanceof Date ? m.timestamp.toISOString() : String(m.timestamp),
        })),
        cart,
        updatedAt: Date.now(),
      };
      window.localStorage.setItem(storageKey, JSON.stringify(payload));
    } catch {
      // storage denied (embed context) — resume UX degrades gracefully
    }
  }, [storageKey, conversationId, serviceConversationId, mode, activeAgent, isComplete, contactCollected, messages, cart]);

  const invokeEdgeFunction = useCallback(async (
    fnName: string,
    body: Record<string, unknown>,
  ) => {
    const requestBody = { ...body, organizationId };

    const { data, error } = await supabase.functions.invoke(fnName, {
      body: requestBody,
    });

    if (error) {
      throw new Error(error.message || 'Edge function invocation failed');
    }

    if (data?.error) {
      throw new Error(data.error);
    }

    return data;
  }, [organizationId]);

  const appendAssistantMessage = useCallback((payload: {
    content: string;
    agentType?: string;
    actionCards?: AgentCardData[];
    cartSnapshot?: CartSnapshot | null;
  }) => {
    const msg: AgentMessage = {
      id: `msg-${Date.now()}-assistant`,
      role: 'assistant',
      content: payload.content,
      timestamp: new Date(),
      agentType: payload.agentType,
      actionCards: payload.actionCards,
      cartSnapshot: payload.cartSnapshot ?? undefined,
    };
    setMessages(prev => [...prev, msg]);
  }, []);

  const mapServiceActionCard = useCallback((
    actionCard: { type: string; data: any } | null | undefined,
    machines: ServiceMachineData[] | null | undefined,
    packages: ServicePackageData[] | null | undefined,
  ): AgentCardData[] | undefined => {
    const cards: AgentCardData[] = [];
    if (actionCard?.type === 'customer_info_form') {
      cards.push({ type: 'customer_info_form', data: actionCard.data || {} });
    } else if (actionCard?.type === 'verification_code_prompt') {
      cards.push({ type: 'verification_code_prompt', data: actionCard.data });
    } else if (actionCard?.type === 'customer_confirm') {
      cards.push({ type: 'customer_confirm', data: actionCard.data });
    } else if (actionCard?.type === 'equipment_confirm') {
      cards.push({ type: 'equipment_confirm', data: actionCard.data });
    } else if (actionCard?.type === 'package_selection') {
      cards.push({ type: 'package_selection', data: actionCard.data });
    } else if (actionCard?.type === 'machine_selection') {
      cards.push({ type: 'machine_selection', data: actionCard.data });
    }
    // Some responses emit `machines` separately (alongside customer_confirm)
    if (machines && machines.length > 0 && !cards.some(c => c.type === 'machine_selection')) {
      cards.push({ type: 'machine_selection', data: { machines } });
    }
    // Packages emitted outside action_card get folded in too
    if (packages && packages.length > 0 && !cards.some(c => c.type === 'package_selection')) {
      cards.push({ type: 'package_selection', data: { packages } });
    }
    return cards.length > 0 ? cards : undefined;
  }, []);

  const sendServiceMessage = useCallback(async (message: string, agentConvId?: string | null) => {
    // Machine QR hub: the sticker's machine seeds the service pipeline's equipment (read once, at its
    // first turn) and a verified hub identity stands in for the contact form (re-validated server-side).
    const response: ServiceChatResponse = await invokeEdgeFunction(SERVICE_EDGE_FUNCTION, withHub(withMachine({
      action: 'chat',
      message,
      conversationId: serviceConversationId,
      // Threads the service flow onto the agent conversation's lead so both
      // pipelines share one lead and one transcript. On the swap turn the
      // caller passes the id directly because the conversationId state
      // update hasn't committed yet.
      agentConversationId: agentConvId ?? conversationId,
    }, machineContext), hubIdentity));
    if (response.conversation_id) {
      setServiceConversationId(response.conversation_id);
    }
    // The service flow collects contact via its form; the pipeline state
    // carries the fields once submitted. A hub-verified visitor (machine QR
    // hub) is already a known customer, so the End nudge must not re-ask.
    const serviceState = (response.state || {}) as Record<string, unknown>;
    if (serviceState.customer_email || serviceState.customer_phone || serviceState.hub_verified === true) {
      setContactCollected(true);
    }
    appendAssistantMessage({
      content: response.message,
      agentType: 'service',
      actionCards: mapServiceActionCard(
        response.action_card,
        response.machines,
        response.packages,
      ),
    });
    if (response.complete) {
      setIsComplete(true);
    }
  }, [invokeEdgeFunction, serviceConversationId, conversationId, machineContext, hubIdentity, appendAssistantMessage, mapServiceActionCard]);

  const sendAgentMessage = useCallback(async (message: string) => {
    // Machine QR hub: the sticker's machine, so the agents know what "my tractor" is.
    const response: AgentChatResponse = await invokeEdgeFunction(AGENT_EDGE_FUNCTION, withMachine({
      action: 'chat',
      message,
      conversationId,
    }, machineContext));
    if (response.conversation_id) {
      setConversationId(response.conversation_id);
    }
    if (response.active_agent) {
      setActiveAgent(response.active_agent);
    }
    if (typeof response.contact_collected === 'boolean' && response.contact_collected) {
      setContactCollected(true);
    }
    if (response.cart_snapshot !== undefined && response.cart_snapshot !== null) {
      setCart(response.cart_snapshot);
    }

    // If the general agent just decided the customer wants service, swap modes
    // and bootstrap the service conversation with the same message. The
    // service pipeline starts fresh so it can parse the message into its state
    // machine cleanly rather than inheriting the general agent's reply.
    if (response.active_agent === 'service') {
      setMode('service');
      await sendServiceMessage(message, response.conversation_id);
      return;
    }

    appendAssistantMessage({
      content: response.message,
      agentType: response.active_agent,
      actionCards: response.action_cards || undefined,
      cartSnapshot: response.cart_snapshot,
    });

    if (response.complete) {
      setIsComplete(true);
    }
  }, [
    invokeEdgeFunction,
    conversationId,
    machineContext,
    appendAssistantMessage,
    sendServiceMessage,
  ]);

  const sendMessage = useCallback(async (message: string) => {
    // Once the conversation has ended, stale card buttons must be no-ops —
    // the server would otherwise resurrect a completed conversation.
    if (!message.trim() || loading || isComplete) return;

    setLoading(true);
    setError(null);

    const userMsg: AgentMessage = {
      id: `msg-${Date.now()}-user`,
      role: 'user',
      content: message.trim(),
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMsg]);

    try {
      if (mode === 'service') {
        await sendServiceMessage(message.trim());
      } else {
        await sendAgentMessage(message.trim());
      }
      setSuggestedActions([]);
    } catch (err: any) {
      console.error('Agent chat error:', err?.message);
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [loading, isComplete, mode, sendAgentMessage, sendServiceMessage]);

  /**
   * Submit a customer-info form rendered inside the chat. Sends a JSON
   * payload the backend can parse structurally (skipping LLM extraction
   * for sensitive contact fields), while displaying a friendly message
   * in the chat history rather than raw JSON.
   */
  const submitCustomerInfoForm = useCallback(async (values: CustomerInfoFormValues) => {
    if (loading) return;

    setLoading(true);
    setError(null);

    const friendly: AgentMessage = {
      id: `msg-${Date.now()}-user`,
      role: 'user',
      content: 'Submitted my information.',
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, friendly]);

    try {
      const payload = JSON.stringify({ __form__: 'customer_info', ...values });
      // The service mode is the only one that parses customer_info_form today.
      // If we're somehow not yet in service mode, fall through to agent mode —
      // the general agent will treat it as a no-op string and keep going.
      if (mode === 'service') {
        await sendServiceMessage(payload);
      } else {
        await sendAgentMessage(payload);
      }
      setSuggestedActions([]);
    } catch (err: any) {
      console.error('Agent form submit error:', err?.message);
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [loading, mode, sendAgentMessage, sendServiceMessage]);

  /** Submit a 6-digit code from the verification_code_prompt card. */
  const submitVerificationCode = useCallback(async (code: string) => {
    if (loading) return;
    setLoading(true);
    setError(null);
    const friendly: AgentMessage = {
      id: `msg-${Date.now()}-user`,
      role: 'user',
      content: 'Submitted the verification code.',
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, friendly]);
    try {
      await sendServiceMessage(JSON.stringify({ __verify__: code }));
    } catch (err: any) {
      console.error('Verify submit error:', err?.message);
      setError('Something went wrong verifying the code. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [loading, sendServiceMessage]);

  const skipVerification = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    setError(null);
    const friendly: AgentMessage = {
      id: `msg-${Date.now()}-user`,
      role: 'user',
      content: 'Continue without verification.',
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, friendly]);
    try {
      await sendServiceMessage(JSON.stringify({ __verify_skip__: true }));
    } catch (err: any) {
      console.error('Verify skip error:', err?.message);
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [loading, sendServiceMessage]);

  const resendVerificationCode = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    setError(null);
    const friendly: AgentMessage = {
      id: `msg-${Date.now()}-user`,
      role: 'user',
      content: 'Resend the verification code.',
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, friendly]);
    try {
      await sendServiceMessage(JSON.stringify({ __verify_resend__: true }));
    } catch (err: any) {
      console.error('Verify resend error:', err?.message);
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [loading, sendServiceMessage]);

  const endConversation = useCallback(async () => {
    if (!conversationId) return;

    // One gentle nudge before ending without contact info: the lead exists
    // either way (progressive capture), but a reachable lead is worth one ask.
    // A second End click ends regardless.
    if (!contactCollected && !endNudgeShown && messages.length > 0) {
      setEndNudgeShown(true);
      appendAssistantMessage({
        content:
          "Before you go — could I get your name and the best email or phone to reach you? " +
          "That way our team can follow up on this. Or press End again to finish without it.",
      });
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await invokeEdgeFunction(AGENT_EDGE_FUNCTION, {
        action: 'end',
        conversationId,
      });

      // Add farewell message
      const farewell: AgentMessage = {
        id: `msg-${Date.now()}-end`,
        role: 'assistant',
        content: response.message || 'Thank you for chatting with us!',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, farewell]);
      setIsComplete(true);
      setSuggestedActions([]);
    } catch (err: any) {
      console.error('Agent end conversation error:', err?.message);
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [conversationId, invokeEdgeFunction, contactCollected, endNudgeShown, messages.length, appendAssistantMessage]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    messages,
    conversationId,
    activeAgent,
    loading,
    error,
    isComplete,
    suggestedActions,
    cart,
    sendMessage,
    submitCustomerInfoForm,
    submitVerificationCode,
    skipVerification,
    resendVerificationCode,
    endConversation,
    clearError,
  };
};
